import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend,
} from 'recharts';
import { Calendar, Activity } from 'lucide-react';
import { CATEGORICAL_PALETTE, STATUS_COLORS } from '../../utils/chartColors';
import AnalyticsHeroCard from './AnalyticsHeroCard';
import ModuleDistributionChart from './ModuleDistributionChart';

interface DeploymentChartProps {
  monthlyData: Array<{
    month: string;
    deployments: number;
    successful: number;
    failed: number;
  }>;
  dailyData: Array<{
    date: string;
    deployments: number;
    pending: number;
    approved: number;
    deployed: number;
  }>;
  moduleData: Array<{
    name: string;
    value: number;
    color: string;
  }>;
}

const CARD_CLASS = 'bg-white rounded-lg border border-gray-200 p-6';

const ChartHeader: React.FC<{ icon: React.ElementType; iconClass: string; title: string; caption: string }> = ({
  icon: Icon, iconClass, title, caption,
}) => (
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center gap-2">
      <div className={`p-1.5 rounded-lg ${iconClass}`}>
        <Icon className="w-4 h-4" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
    </div>
    <div className="text-sm text-gray-500">{caption}</div>
  </div>
);

const DeploymentChart: React.FC<DeploymentChartProps> = ({ monthlyData, dailyData, moduleData }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Monthly Deployments */}
      <div className={CARD_CLASS}>
        <ChartHeader icon={Calendar} iconClass="bg-blue-100 text-blue-600" title="Monthly Deployments" caption="Last 6 months" />
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={monthlyData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e1e0d9" />
            <XAxis dataKey="month" tick={{ fill: '#898781', fontSize: 12 }} />
            <YAxis tick={{ fill: '#898781', fontSize: 12 }} allowDecimals={false} />
            <Tooltip />
            <Legend />
            <Bar dataKey="successful" stackId="a" fill={STATUS_COLORS.good} name="Successful" radius={[0, 0, 0, 0]} />
            <Bar dataKey="failed" stackId="a" fill={STATUS_COLORS.critical} name="Failed" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Analytics Hero — replaces the old Daily Trend line chart with a
          rotating KPI summary card (3-dot carousel: this month / last 30
          days / 6-month trend), styled after the requested reference image. */}
      <AnalyticsHeroCard monthlyData={monthlyData} dailyData={dailyData} moduleData={moduleData} />

      {/* Module Distribution */}
      <div className={CARD_CLASS}>
        <ChartHeader icon={Activity} iconClass="bg-violet-100 text-violet-600" title="Module Distribution" caption="This month" />
        {moduleData.length === 0 ? (
          <div className="flex items-center justify-center h-[300px] text-gray-400 text-sm">
            No deployments this month yet
          </div>
        ) : (
          <ModuleDistributionChart data={moduleData} />
        )}
      </div>

      {/* Deployment Status Overview */}
      <div className={CARD_CLASS}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Status Overview</h3>
          <div className="text-sm text-gray-500">Current month</div>
        </div>
        <div className="flex items-center gap-4 mb-3 text-xs text-gray-500">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: STATUS_COLORS.warning }} />
            Pending
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CATEGORICAL_PALETTE[0] }} />
            Approved
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: STATUS_COLORS.good }} />
            Deployed
          </span>
        </div>
        <div className="space-y-3">
          {dailyData.slice(-7).map((day, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-900">{day.date}</div>
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-1.5">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: STATUS_COLORS.warning }} />
                  <span className="text-xs text-gray-600 tabular-nums">{day.pending}</span>
                </div>
                <div className="flex items-center space-x-1.5">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CATEGORICAL_PALETTE[0] }} />
                  <span className="text-xs text-gray-600 tabular-nums">{day.approved}</span>
                </div>
                <div className="flex items-center space-x-1.5">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: STATUS_COLORS.good }} />
                  <span className="text-xs text-gray-600 tabular-nums">{day.deployed}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DeploymentChart;
